// Import skill lists
import { ADMINISTRATIVE_SKILLS_CATEGORIES } from './skills/administrativeSkills';
import { AGRICULTURE_FARMING_SKILLS_CATEGORIES } from './skills/agricultureFarmingSkills';
import { ANALYTICAL_SKILLS_CATEGORIES } from './skills/analyticalSkills';
import { ARMED_FORCES_POLICE_SKILLS_CATEGORIES } from './skills/armedForcePoliceSkills';
import { COMMUNICATION_SKILLS_CATEGORIES } from './skills/communicationSkills';
import { CREATIVE_SKILLS_CATEGORIES } from './skills/creativeSkills';
import { CUSTOMER_SERVICE_SKILLS_CATEGORIES } from './skills/customerServiceSkills';
import { DIGITAL_MARKETING_SKILLS_CATEGORIES } from './skills/digitalMarketingSkills';
import { EDUCATION_SKILLS_CATEGORIES } from './skills/educationSkills';
import { ENGINEERING_SKILLS_CATEGORIES } from './skills/engineeringSkills';
import { ENVIRONMENTAL_SUSTAINABILITY_SKILLS_CATEGORIES } from './skills/environmentalSustainabilitySkills';
import { FINANCIAL_SKILLS_CATEGORIES } from './skills/financialSkills';
import { HAIR_BEAUTY_SKILLS_CATEGORIES } from './skills/hairBeautySkills';
import { HEALTHCARE_SKILLS_CATEGORIES } from './skills/healthcareSkills';
import { HERITAGE_CRAFTS_SKILLS_CATEGORIES } from './skills/heritageCraftsSkills';
import { HUMAN_RESOURCES_SKILLS_CATEGORIES } from './skills/humanResourcesSkills';
import { INFORMATION_TECHNOLOGY_SKILLS_CATEGORIES } from './skills/informationTechnologySkills';
import { INTERPERSONAL_SKILLS_CATEGORIES } from './skills/interpersonalSkills';
import { LANGUAGE_SKILLS_CATEGORIES } from './skills/languageSkills';
import { LEADERSHIP_SKILLS_CATEGORIES } from './skills/leadershipSkills';
import { LEGAL_SKILLS_CATEGORIES } from './skills/legalSkills';
import { LOGISTICS_SKILLS_CATEGORIES } from './skills/logisticsSkills';
import { MANUFACTURING_SKILLS_CATEGORIES } from './skills/manufacturingSkills';
import { PROJECT_MANAGEMENT_SKILLS_CATEGORIES } from './skills/projectManagementSkills';
import { RESEARCH_DEVELOPMENT_SKILLS_CATEGORIES } from './skills/researchDevelopmentSkills';
import { SALES_MARKETING_SKILLS_CATEGORIES } from './skills/salesMarketingSkills';
import { TECHNICAL_SKILLS_CATEGORIES } from './skills/technicalSkills';

// Comprehensive Global Skill Taxonomy
export interface SkillNode {
  id: string;
  name: string;
  description?: string;
  children?: SkillNode[];
  complementarySkills?: SkillNode[];
  prerequisiteSkills?: SkillNode[];
  fullPath?: string[];
}

export interface SkillCategory {
  name: string;
  skills: string[];
}

// Helper function to generate sub-skills with proper IDs and categories
function generateSubSkills(parentId: string, categories: SkillCategory[]): SkillNode[] {
  return categories.map((category, index) => ({
    id: `${parentId}-${index}`,
    name: category.name,
    children: category.skills.map((skill, skillIndex) => ({
      id: `${parentId}-${index}-${skillIndex}`,
      name: skill
    }))
  }));
}

export const GLOBAL_SKILL_TAXONOMY: SkillNode = {
  id: 'root',
  name: 'All Skills',
  children: [
    {
      id: 'administrative',
      name: 'Administrative Skills',
      description: 'Skills related to office management, organization, and administrative tasks',
      complementarySkills: [
        { id: 'communication', name: 'Communication Skills' },
        { id: 'time_management', name: 'Time Management' }
      ],
      prerequisiteSkills: [
        { id: 'basic_computer_skills', name: 'Basic Computer Skills' }
      ],
      children: ADMINISTRATIVE_SKILLS_CATEGORIES.map((category, index) => ({
        id: `administrative-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `administrative-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'agriculture-farming',
      name: 'Agriculture & Farming Skills',
      description: 'Skills in crop cultivation, animal husbandry, and sustainable farming practices',
      complementarySkills: [
        { id: 'environmental_sustainability', name: 'Environmental Sustainability' },
        { id: 'mechanical_skills', name: 'Mechanical Skills' }
      ],
      prerequisiteSkills: [
        { id: 'soil_science', name: 'Soil Science' }
      ],
      children: AGRICULTURE_FARMING_SKILLS_CATEGORIES.map((category, index) => ({
        id: `agriculture-farming-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `agriculture-farming-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'communication',
      name: 'Communication Skills',
      description: 'Skills for effective verbal, written, and digital communication across various professional contexts including business, technical, and cross-cultural communication',
      complementarySkills: [
        { id: 'public_speaking', name: 'Public Speaking' },
        { id: 'interpersonal_skills', name: 'Interpersonal Skills' }
      ],
      prerequisiteSkills: [
        { id: 'language_skills', name: 'Language Skills' }
      ],
      children: COMMUNICATION_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'communication'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'communication'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'leadership',
      name: 'Leadership Skills',
      description: 'Skills in strategic leadership, team management, organizational development, change management, and ethical leadership',
      complementarySkills: [
        { id: 'project_management', name: 'Project Management' },
        { id: 'coaching', name: 'Coaching' }
      ],
      prerequisiteSkills: [
        { id: 'team_management', name: 'Team Management' }
      ],
      children: LEADERSHIP_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'leadership'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'leadership'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'project-management',
      name: 'Project Management Skills',
      description: 'Skills in project planning, execution, resource management, risk management, and project methodologies',
      complementarySkills: [
        { id: 'agile_methodologies', name: 'Agile Methodologies' },
        { id: 'scrum_master', name: 'Scrum Master' }
      ],
      prerequisiteSkills: [
        { id: 'project_planning', name: 'Project Planning' }
      ],
      children: PROJECT_MANAGEMENT_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'project-management'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'project-management'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'technical',
      name: 'Technical Skills',
      description: 'Skills in software development, web development, database management, system administration, cloud computing, and other technical domains',
      complementarySkills: [
        { id: 'data_science', name: 'Data Science' },
        { id: 'artificial_intelligence', name: 'Artificial Intelligence' }
      ],
      prerequisiteSkills: [
        { id: 'programming_skills', name: 'Programming Skills' }
      ],
      children: TECHNICAL_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'technical'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'technical'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'creative',
      name: 'Creative Skills',
      description: 'Skills in visual design, digital art, traditional art, photography, video production, animation, and other creative domains',
      complementarySkills: [
        { id: 'graphic_design', name: 'Graphic Design' },
        { id: 'user_experience', name: 'User Experience' }
      ],
      prerequisiteSkills: [
        { id: 'artistic_skills', name: 'Artistic Skills' }
      ],
      children: CREATIVE_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'creative'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'creative'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'analytical',
      name: 'Analytical Skills',
      description: 'Core analytical and problem-solving skills including data analysis, research methods, business analysis, and decision making',
      complementarySkills: [
        { id: 'data_analysis', name: 'Data Analysis' },
        { id: 'business_intelligence', name: 'Business Intelligence' }
      ],
      prerequisiteSkills: [
        { id: 'mathematical_skills', name: 'Mathematical Skills' }
      ],
      children: ANALYTICAL_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'analytical'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'analytical'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'sales-marketing',
      name: 'Sales & Marketing Skills',
      description: 'Skills in sales strategy, marketing techniques, digital marketing, content creation, and customer relationship management',
      complementarySkills: [
        { id: 'digital_marketing', name: 'Digital Marketing' },
        { id: 'sales_strategy', name: 'Sales Strategy' }
      ],
      prerequisiteSkills: [
        { id: 'customer_service', name: 'Customer Service' }
      ],
      children: SALES_MARKETING_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'sales-marketing'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'sales-marketing'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'financial',
      name: 'Financial Skills',
      description: 'Skills in accounting, financial analysis, investment management, corporate finance, banking operations, and other financial domains',
      complementarySkills: [
        { id: 'accounting', name: 'Accounting' },
        { id: 'financial_analysis', name: 'Financial Analysis' }
      ],
      prerequisiteSkills: [
        { id: 'mathematical_skills', name: 'Mathematical Skills' }
      ],
      children: FINANCIAL_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'financial'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'financial'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'customer-service',
      name: 'Customer Service Skills',
      description: 'Skills essential for delivering excellent customer service, including communication, problem resolution, customer relations, technical support, sales support, service operations, and quality assurance',
      complementarySkills: [
        { id: 'communication_skills', name: 'Communication Skills' },
        { id: 'problem_solving', name: 'Problem Solving' }
      ],
      prerequisiteSkills: [
        { id: 'customer_relations', name: 'Customer Relations' }
      ],
      children: CUSTOMER_SERVICE_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'customer-service'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'customer-service'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'interpersonal',
      name: 'Interpersonal Skills',
      description: 'Skills in communication, relationship building, emotional intelligence, conflict resolution, leadership, and professional etiquette',
      complementarySkills: [
        { id: 'emotional_intelligence', name: 'Emotional Intelligence' },
        { id: 'conflict_resolution', name: 'Conflict Resolution' }
      ],
      prerequisiteSkills: [
        { id: 'communication_skills', name: 'Communication Skills' }
      ],
      children: INTERPERSONAL_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'interpersonal'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'interpersonal'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'language',
      name: 'Language Skills',
      description: 'Skills in speaking, listening, reading, writing, grammar, vocabulary, translation, and cultural understanding across languages',
      complementarySkills: [
        { id: 'translation', name: 'Translation' },
        { id: 'interpretation', name: 'Interpretation' }
      ],
      prerequisiteSkills: [
        { id: 'language_foundation', name: 'Language Foundation' }
      ],
      children: LANGUAGE_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'language'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'language'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'education',
      name: 'Education Skills',
      description: 'Skills related to teaching, learning, curriculum development, educational technology, special education, student support, and educational leadership',
      complementarySkills: [
        { id: 'curriculum_development', name: 'Curriculum Development' },
        { id: 'educational_technology', name: 'Educational Technology' }
      ],
      prerequisiteSkills: [
        { id: 'teaching_skills', name: 'Teaching Skills' }
      ],
      children: EDUCATION_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'education'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'education'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'legal',
      name: 'Legal Skills',
      description: 'Skills in legal research, litigation, contract law, corporate law, intellectual property, and regulatory compliance',
      complementarySkills: [
        { id: 'contract_law', name: 'Contract Law' },
        { id: 'corporate_law', name: 'Corporate Law' }
      ],
      prerequisiteSkills: [
        { id: 'legal_research', name: 'Legal Research' }
      ],
      children: LEGAL_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'legal'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'legal'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'human-resources',
      name: 'Human Resources Skills',
      description: 'Skills in recruitment, employee relations, training and development, compensation and benefits, HR administration, strategy, and compliance',
      complementarySkills: [
        { id: 'recruitment', name: 'Recruitment' },
        { id: 'employee_relations', name: 'Employee Relations' }
      ],
      prerequisiteSkills: [
        { id: 'hr_administration', name: 'HR Administration' }
      ],
      children: HUMAN_RESOURCES_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'human-resources'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'human-resources'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'healthcare',
      name: 'Healthcare Skills',
      description: 'Skills in clinical care, medical knowledge, healthcare technology, patient care, healthcare administration, public health, and specialized medical fields',
      complementarySkills: [
        { id: 'clinical_care', name: 'Clinical Care' },
        { id: 'medical_knowledge', name: 'Medical Knowledge' }
      ],
      prerequisiteSkills: [
        { id: 'patient_care', name: 'Patient Care' }
      ],
      children: HEALTHCARE_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'healthcare'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'healthcare'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing Skills',
      description: 'Skills in production management, quality control, process improvement, equipment maintenance, and manufacturing technology',
      complementarySkills: [
        { id: 'production_management', name: 'Production Management' },
        { id: 'quality_control', name: 'Quality Control' }
      ],
      prerequisiteSkills: [
        { id: 'equipment_maintenance', name: 'Equipment Maintenance' }
      ],
      children: MANUFACTURING_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'manufacturing'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'manufacturing'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'engineering',
      name: 'Engineering Skills',
      description: 'Technical skills across various engineering disciplines including mechanical, electrical, civil, software, and specialized engineering fields',
      complementarySkills: [
        { id: 'mechanical_engineering', name: 'Mechanical Engineering' },
        { id: 'electrical_engineering', name: 'Electrical Engineering' }
      ],
      prerequisiteSkills: [
        { id: 'mathematical_skills', name: 'Mathematical Skills' }
      ],
      children: ENGINEERING_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'engineering'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'engineering'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'logistics',
      name: 'Logistics Skills',
      description: 'Skills in supply chain management, warehouse operations, transportation, inventory control, and distribution operations',
      complementarySkills: [
        { id: 'supply_chain_management', name: 'Supply Chain Management' },
        { id: 'warehouse_operations', name: 'Warehouse Operations' }
      ],
      prerequisiteSkills: [
        { id: 'inventory_control', name: 'Inventory Control' }
      ],
      children: LOGISTICS_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'logistics'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'logistics'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'information-technology',
      name: 'Information Technology Skills',
      description: 'Comprehensive technical skills covering software development, database management, networking, cloud computing, cybersecurity, and other IT domains',
      complementarySkills: [
        { id: 'software_development', name: 'Software Development' },
        { id: 'database_management', name: 'Database Management' }
      ],
      prerequisiteSkills: [
        { id: 'programming_skills', name: 'Programming Skills' }
      ],
      children: INFORMATION_TECHNOLOGY_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'information-technology'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'information-technology'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'digital-marketing',
      name: 'Digital Marketing Skills',
      description: 'Skills for modern digital marketing including SEO, content marketing, social media, email marketing, paid advertising, analytics, automation, and digital strategy',
      complementarySkills: [
        { id: 'seo', name: 'SEO' },
        { id: 'content_marketing', name: 'Content Marketing' }
      ],
      prerequisiteSkills: [
        { id: 'digital_marketing_fundamentals', name: 'Digital Marketing Fundamentals' }
      ],
      children: DIGITAL_MARKETING_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'digital-marketing'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'digital-marketing'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'research-development',
      name: 'Research & Development Skills',
      description: 'Skills in research methodology, data analysis, product development, laboratory work, and innovation management',
      complementarySkills: [
        { id: 'research_methodology', name: 'Research Methodology' },
        { id: 'data_analysis', name: 'Data Analysis' }
      ],
      prerequisiteSkills: [
        { id: 'scientific_knowledge', name: 'Scientific Knowledge' }
      ],
      children: RESEARCH_DEVELOPMENT_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'research-development'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'research-development'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'environmental-sustainability',
      name: 'Environmental Sustainability Skills',
      description: 'Skills focused on environmental protection, sustainability, resource management, renewable energy, and climate change mitigation',
      complementarySkills: [
        { id: 'environmental_protection', name: 'Environmental Protection' },
        { id: 'sustainability', name: 'Sustainability' }
      ],
      prerequisiteSkills: [
        { id: 'environmental_science', name: 'Environmental Science' }
      ],
      children: ENVIRONMENTAL_SUSTAINABILITY_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'environmental-sustainability'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'environmental-sustainability'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'armed-forces-police',
      name: 'Armed Forces and Police Service Skills',
      description: 'Skills specific to military and law enforcement operations, including combat, law enforcement, physical training, weapons training, intelligence operations, and security management',
      complementarySkills: [
        { id: 'combat_skills', name: 'Combat Skills' },
        { id: 'law_enforcement', name: 'Law Enforcement' }
      ],
      prerequisiteSkills: [
        { id: 'physical_training', name: 'Physical Training' }
      ],
      children: ARMED_FORCES_POLICE_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'armed-forces-police'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'armed-forces-police'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'heritage-crafts',
      name: 'Heritage Crafts Skills',
      description: 'Traditional craftsmanship, artisanal techniques, and cultural preservation methods passed down through generations, including woodworking, metalworking, textiles, ceramics, and other traditional crafts',
      complementarySkills: [
        { id: 'woodworking', name: 'Woodworking' },
        { id: 'metalworking', name: 'Metalworking' }
      ],
      prerequisiteSkills: [
        { id: 'traditional_crafts', name: 'Traditional Crafts' }
      ],
      children: HERITAGE_CRAFTS_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'heritage-crafts'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'heritage-crafts'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    },
    {
      id: 'hair-beauty',
      name: 'Hair & Beauty Skills',
      description: 'Skills in hair styling, skincare, makeup artistry, nail care, body treatments, and other beauty-related services',
      complementarySkills: [
        { id: 'hair_styling', name: 'Hair Styling' },
        { id: 'skincare', name: 'Skincare' }
      ],
      prerequisiteSkills: [
        { id: 'beauty_fundamentals', name: 'Beauty Fundamentals' }
      ],
      children: HAIR_BEAUTY_SKILLS_CATEGORIES.map((category, index) => ({
        id: `${'hair-beauty'}-${index}`,
        name: category.name,
        children: category.skills.map((skill, skillIndex) => ({
          id: `${'hair-beauty'}-${index}-${skillIndex}`,
          name: skill
        }))
      }))
    }
  ]
};

// Utility function to flatten taxonomy for searching
export function flattenSkillTaxonomy(
  node: SkillNode = GLOBAL_SKILL_TAXONOMY,
  parentPath: string[] = []
): Array<{
  id: string;
  name: string;
  fullPath: string[];
}> {
  let result = [{
    id: node.id,
    name: node.name,
    fullPath: parentPath
  }];

  if (node.children) {
    for (const child of node.children) {
      result = result.concat(flattenSkillTaxonomy(child, [...parentPath, node.name]));
    }
  }

  return result;
}

// Utility function to find a skill by ID
export function findSkillById(
  id: string,
  node: SkillNode = GLOBAL_SKILL_TAXONOMY
): SkillNode | null {
  if (node.id === id) return node;
  
  if (node.children) {
    for (const child of node.children) {
      const found = findSkillById(id, child);
      if (found) return found;
    }
  }
  
  return null;
}

// Optional: Create a flat list of all skill names for quick reference
export const ALL_SKILLS = flattenSkillTaxonomy()
  .filter(skill => skill.id !== 'root')
  .map(skill => skill.name);
